<template>
    <div class="sv2">
        <!-- 저장 바 -->
        <div class="sv2-savebar">
            <span class="code" :title="`sub_code — 고유 키`">{{ subCode || '(문항 없음)' }}</span>
            <span class="nm">{{ subName }}</span>
            <span class="state" :class="stateClass">
                <span class="dot"></span>{{ stateText }}
                <button v-if="saveFailed" class="btn tiny retry" @click="save">다시 시도</button>
            </span>
            <button class="btn" :disabled="!anyDirty" @click="confirmReload">되돌리기</button>
            <button class="btn primary" :disabled="!dirty && !scoreDirty" @click="save">저장</button>
            <!-- 모달 안에서만 의미가 있다 — 부모가 cancel 을 듣고 있을 때만 그린다 -->
            <button v-if="$listeners.cancel" class="btn" @click="$emit('cancel')">닫기</button>
        </div>
        <p v-if="flash" class="flash" :class="{fail: saveFailed}">{{ flash }}<button class="btn tiny" @click="flash = ''">닫기</button></p>
        <p v-if="loading" class="muted">불러오는 중…</p>
        <p v-if="loadError" class="err">{{ loadError }}</p>

        <!-- 변환 노트 -->
        <div v-if="notes.length" class="sv2-notes">
            <b>변환하며 확인한 것</b>
            <ul><li v-for="(n, i) in notes" :key="i">{{ n }}</li></ul>
        </div>

        <div class="sv2-split" :class="{'wide-mode': tab !== 'edit'}">
            <!-- 실제 응답자 화면 (권위 있는 미리보기) -->
            <div class="sv2-card sv2-preview">
                <div class="hd">
                    <span>협력사 화면</span>
                    <span class="r">실제 응답자 컴포넌트로 렌더 · 편집하면 즉시 반영</span>
                </div>
                <div class="pv">
                    <sub-component v-if="previewSub"
                                   :key="previewKey"
                                   :sub_type="previewSub.sub_type"
                                   :item="previewSub"
                                   :preview="true"
                                   :view_pipe_line="false"
                                   :is_disabled_by_parent="true"
                                   :skip_info="true" />
                    <p v-else class="muted">미리보기를 만들 수 없습니다.</p>
                </div>
            </div>

            <!-- 구조 -->
            <div class="sv2-card">
                <div class="hd">
                    <span>구조</span>
                    <div class="seg">
                        <button type="button" :aria-pressed="String(mode==='preview')" @click="mode='preview'">미리보기</button>
                        <button type="button" :aria-pressed="String(mode==='struct')" @click="mode='struct'">구조 보기</button>
                    </div>
                    <span class="r">노드 {{ nodeCount }}개 · 깊이 {{ depth }}단계</span>
                </div>
                <div class="canvas" :class="{showstruct: mode==='struct'}">
                    <p v-if="!root" class="muted pad">문항을 불러오거나 “예시로 보기”를 누르세요.</p>
                    <sv2-node v-else :node="root" :sel="tab === 'score' ? null : selId" :marks="condMarks"
                              :show-struct="mode === 'struct'"
                              @pick="onPick" @add-column="addColIn" @move-column="moveColIn"
                              @add-row="addRowIn" />
                    <p v-if="tab === 'score' && pickMode === 'target'" class="pickhint">
                        <b>조건의 대상을 클릭하세요</b> — 문항의 답, 표의 입력 칸, 선택지 안의 입력.
                    </p>
                    <p v-else-if="tab === 'score' && pickMode === 'evidence'" class="pickhint evid">
                        📎 <b>증빙이 붙을 자리를 클릭하세요</b> — 선택지, 선택지 안 요소, 표의 행.
                    </p>
                </div>
            </div>

            <!-- 인스펙터 -->
            <div class="sv2-card insp">
                <div class="hd">
                    <span>{{ tab === 'score' ? '채점' : tab === 'i18n' ? '번역' : '선택한 노드' }}</span>
                    <div class="seg">
                        <button type="button" :aria-pressed="String(tab==='edit')" @click="tab='edit'; pickMode=null">편집</button>
                        <button type="button" :aria-pressed="String(tab==='score')" @click="tab='score'">채점</button>
                        <button type="button" :aria-pressed="String(tab==='i18n')" @click="tab='i18n'; pickMode=null">
                            번역<span v-if="i18nMissing" class="segcnt" :title="'빈 번역 ' + i18nMissing + '칸'">{{ i18nMissing }}</span>
                        </button>
                    </div>
                </div>
                <div class="bd">

                    <!-- ═══ 채점 ═══ -->
                    <!-- v-show — 탭을 오가도 저장하지 않은 평가요소 편집이 사라지지 않게 파괴하지 않는다 -->
                    <div v-show="tab === 'score'">
                        <p v-if="!root" class="muted">문항을 먼저 불러오세요.</p>
                        <template v-else>
                            <template v-if="evalsError">
                                <p class="h err">{{ evalsError }}</p>
                                <button class="btn" @click="loadEvals(subCode)">다시 불러오기</button>
                            </template>
                            <!-- 평가요소를 못 읽었으면 편집·저장을 막는다(빈 목록이 서버의 채점 정의를 덮어쓰지 않게) -->
                            <sv2-score v-else-if="evalsLoaded" ref="score" :root="root" :targets="targets" :ref-paths="refPaths" :initial="evals"
                                       :sub-meta="subMeta" :ev-codes="evCodes" :sub-code="subCode"
                                       @marks="condMarks = $event" @pick-mode="pickMode = $event"
                                       @flash="flash = $event" @save="saveScore" @dirty="scoreDirty = $event" />
                            <p v-else class="muted">평가요소를 불러오는 중…</p>
                        </template>
                    </div>

                    <!-- ═══ 번역 ═══ -->
                    <template v-if="tab === 'i18n'">
                        <p v-if="!root" class="muted">문항을 먼저 불러오세요.</p>
                        <sv2-i18n v-else :root="root" :sub="loadedSub" :sub-code="subCode"
                                  @save="saveI18n" @auto="autoTranslate" @missing="i18nMissing = $event"
                                  @touch="i18nDirty = true" />
                    </template>

                    <!-- ═══ 편집 ═══ -->
                    <p v-else-if="tab === 'edit' && !selNode" class="muted">캔버스에서 노드를 클릭하세요.</p>
                    <template v-else-if="tab === 'edit'">
                        <p class="crumb">{{ crumb }}</p>

                        <div v-if="ownerTable" class="group">
                            <p class="group-hd">
                                표 전체
                                <span v-if="ownerTable !== selNode">— 이 표의 모든 칸에 적용됩니다</span>
                            </p>
                            <div class="f">
                                <label>열 ({{ (ownerTable.columns || []).length }})</label>
                                <div class="cols">
                                    <span v-for="(col, i) in ownerTable.columns" :key="col.id"
                                          class="colchip" :class="{sel: col.id === selId}">
                                        <input type="text" :value="col.label"
                                               :size="Math.max(3, (col.label || '').length)"
                                               :aria-label="`${i + 1}번째 열 이름`"
                                               @focus="selId = col.id"
                                               @input="setCol(i, $event.target.value)">
                                        <button class="x" title="이 열과 각 행의 칸 삭제"
                                                @click="delCol(i)">×</button>
                                    </span>
                                    <button class="btn tiny addcol" @click="addColIn()">+ 열</button>
                                </div>
                                <!-- 순서 조작의 본자리는 표 헤더 드래그다. 여기 버튼은 키보드/보조기기용 대체 경로. -->
                                <p v-if="selColIndex >= 0" class="posrow">
                                    <span class="posname">{{ ownerTable.columns[selColIndex].label || '(이름 없음)' }}</span>
                                    <button class="btn tiny" :disabled="selColIndex === 0"
                                            @click="moveCol(selColIndex, selColIndex - 1)">← 왼쪽</button>
                                    <span class="h" style="margin:0">{{ selColIndex + 1 }} / {{ ownerTable.columns.length }}</span>
                                    <button class="btn tiny" :disabled="selColIndex === ownerTable.columns.length - 1"
                                            @click="moveCol(selColIndex, selColIndex + 1)">오른쪽 →</button>
                                </p>
                                <p class="h">이름을 바꾸면 각 행의 값·번역이 새 이름으로 따라갑니다.
                                    순서는 <b>표 헤더를 끌어서</b>도 바꿀 수 있습니다.</p>
                            </div>
                            <p v-if="!tableRequiredCells.length" class="h">
                                필수 셀을 정하지 않으면 <b>표에 값이 하나만 있어도 통과</b>합니다. 칸을 골라 「필수 셀」을 켜면 그 칸을 비운 채로는 제출이 막힙니다.
                            </p>
                            <p v-else-if="submitOff" class="h err">
                                이 표에 필수 셀이 {{ tableRequiredCells.length }}개 있지만 문항의 제출 차단이 「끔」이라 적용되지 않습니다.
                            </p>
                            <p v-else class="h">필수 셀 {{ tableRequiredCells.length }}개 — 별표가 붙은 칸입니다.</p>

                            <div class="f">
                                <label>행 ({{ (ownerTable.children || []).length }})</label>
                                <button class="btn tiny" @click="addRowIn()">+ 행</button>
                                <p class="h">각 칸의 동작은 칸을 클릭해 정합니다.</p>
                            </div>
                        </div>

                        <!-- 문항 설정 — 구조가 아니라 sub_data_header. 백엔드 POST /subs/init 이 병합하는 키만 둔다
                             (enable_na · enable_na_label · required_answer · has_attachment · required_attachment ·
                              required_opt_attachment · submit_required). 응답자 화면(DFRadio 등)이 이 값으로 동작한다. -->
                        <!-- 문항 정보 — 구조가 아니라 문항 자체의 속성(df_subs_init 컬럼). 저장 payload 에는
                             이미 실려 나가던 값들이라 여기서는 입력만 붙인다. 백엔드·저장 경로 무변경. -->
                        <div v-if="selNode.root && loadedSub" class="group hdr">
                            <p class="group-hd">문항 정보 <span>— 응답자에게 보이는 문구와 문항 속성</span></p>
                            <div class="f">
                                <label>문항 제목</label>
                                <input type="text" :value="subName" placeholder="(예) 간접 고용 여부"
                                       @input="setRootName($event.target.value)">
                                <p class="h">목록과 응답자 화면에 나오는 이름입니다.</p>
                            </div>
                            <div class="f">
                                <label>질문 문구</label>
                                <textarea :value="loadedSub.sub_description || ''" rows="3"
                                          placeholder="(예) 귀사는 인권 정책을 수립하고 있습니까?"
                                          @input="setSubField('sub_description', $event.target.value)"></textarea>
                                <p class="h">협력사가 읽는 <b>질문 문장 그 자체</b>입니다. 영문·중문은 번역 탭에서 답니다.</p>
                            </div>
                            <div class="f">
                                <label>참고 정보</label>
                                <textarea :value="loadedSub.sub_info || ''" rows="3"
                                          placeholder="(예) ※ 참고사항 — 모회사 수준의 승인일 경우 '기타'에 적어 주세요"
                                          @input="setSubField('sub_info', $event.target.value)"></textarea>
                                <p class="h">용어 설명·평가 기준처럼 질문 아래에 덧붙는 안내입니다. HTML 을 쓸 수 있습니다.</p>
                            </div>
                            <div class="f">
                                <label class="cb">
                                    <input type="checkbox" :checked="!!loadedSub.risk"
                                           @change="setSubField('risk', $event.target.checked)">
                                    <span><b>채점 대상</b> — 이 문항의 답을 채점에 넣습니다</span>
                                </label>
                                <p class="h">꺼져 있으면 평가요소를 만들어도 점수가 계산되지 않습니다.</p>
                            </div>
                            <div class="f">
                                <label>문항 라벨</label>
                                <input type="text" :value="loadedSub.label || ''" :placeholder="subCode"
                                       @input="setSubField('label', $event.target.value)">
                                <p class="h">보고서·목록에서 문항을 가리키는 이름입니다. 대개 문항 코드와 같습니다.</p>
                            </div>
                            <div class="f">
                                <label>태그</label>
                                <input type="text" :value="tagsText" placeholder="(예) DJSI,인권"
                                       @input="setTags($event.target.value)">
                                <p class="h">쉼표로 구분합니다. <b>쉼표 뒤에 빈칸을 두지 마세요</b> — 빈칸까지 이름의
                                    일부라 같은 뜻의 태그가 새로 생깁니다.</p>
                            </div>
                        </div>

                        <div v-if="selNode.root && header" class="group hdr">
                            <p class="group-hd">문항 설정 <span>— 응답자 화면 전체에 적용됩니다</span></p>
                            <div class="f">
                                <label class="cb">
                                    <input type="checkbox" :checked="!!header.enable_na" @change="setHeader('enable_na', $event.target.checked)">
                                    <span><b>해당없음 선택지 허용</b> — 응답자가 이 문항을 「해당없음」으로 답할 수 있습니다</span>
                                </label>
                                <div v-if="header.enable_na" class="sub">
                                    <label>해당없음 라벨</label>
                                    <input type="text" :value="header.enable_na_label || ''" placeholder="해당없음"
                                           @input="setHeader('enable_na_label', $event.target.value)">
                                    <p class="h">비우면 「해당없음」으로 표시됩니다. 라벨을 바꾸면 저장 시 영문·중문이 자동 번역됩니다
                                        (번역 탭에서 고칠 수 있습니다). 해당없음으로 답하면 채점에서 <b>해당없음</b>으로 처리됩니다.</p>
                                </div>
                            </div>
                            <div class="f">
                                <label class="cb">
                                    <input type="checkbox" :checked="!!header.required_answer" @change="setHeader('required_answer', $event.target.checked)">
                                    <span><b>답변 필수</b> 표시</span>
                                </label>
                                <label class="cb">
                                    <input type="checkbox" :checked="!!header.has_attachment" @change="setHeader('has_attachment', $event.target.checked)">
                                    <span><b>첨부 허용</b> — 문항에 파일 첨부 칸이 생깁니다</span>
                                </label>
                                <label v-if="header.has_attachment || header.required_opt_attachment" class="cb sub">
                                    <input type="checkbox" :checked="!!header.required_attachment" @change="setHeader('required_attachment', $event.target.checked)">
                                    <span><b>첨부 필수</b> 표시</span>
                                </label>
                                <label v-if="isChoiceParent" class="cb">
                                    <input type="checkbox" :checked="!!header.required_opt_attachment" @change="setHeader('required_opt_attachment', $event.target.checked)">
                                    <span><b>선택지별 첨부</b> — 고른 선택지 옆에 첨부 칸이 붙습니다 (평가요소의 증빙 위치와 짝)</span>
                                </label>
                            </div>
                            <!-- 표시 방식 — 응답자 화면의 생김새. 백엔드 화이트리스트에 있는 키만 저장된다. -->
                            <div v-if="rootType === 'radio'" class="f">
                                <label class="cb">
                                    <input type="checkbox" :checked="!!header.horizontal" @change="setHeader('horizontal', $event.target.checked)">
                                    <span><b>선택지 가로 배치</b> — 끄면 세로로 쌓입니다</span>
                                </label>
                            </div>
                            <div v-if="rootType === 'radio' || rootType === 'checkbox'" class="f">
                                <label>채점 방식</label>
                                <div class="opts">
                                    <span v-for="o in SCORING_MODE" :key="o.v" class="opt" :class="{on: (header.scoring || 'none') === o.v}"
                                          @click="setHeader('scoring', o.v)">{{ o.label }}</span>
                                </div>
                                <p class="h">「선택지별 배점」이면 선택지마다 점수를 매깁니다. 채점 탭의 평가요소와는 다른 경로입니다.</p>
                            </div>
                            <div v-if="rootType === 'textbox' || rootType === 'guide_text' || rootType === 'guide_text2'" class="f">
                                <label>{{ rootType === 'textbox' ? '입력창 줄 수' : '줄 수' }}</label>
                                <input type="number" min="0" :value="header.rows == null ? '' : header.rows" placeholder="8"
                                       @input="setHeader('rows', $event.target.value === '' ? null : Number($event.target.value))">
                                <p class="h">비우면 기본값(8줄)으로 보입니다.</p>
                            </div>
                            <div v-if="rootType === 'guide_text'" class="f">
                                <label class="cb">
                                    <input type="checkbox" :checked="!!header.html" @change="setHeader('html', $event.target.checked)">
                                    <span><b>HTML 로 렌더링</b> — 태그를 글자가 아니라 서식으로 그립니다</span>
                                </label>
                                <label class="cb">
                                    <input type="checkbox" :checked="!!header.bordered" @change="setHeader('bordered', $event.target.checked)">
                                    <span><b>테두리 상자</b> 안에 넣기</span>
                                </label>
                            </div>

                            <!-- guide_text2 의 표시 옵션은 문항 설정이 아니라 문항 내용에 저장된다
                                 (DFGuideText2 가 sub_data_contents 에서 읽는다). guide_text 와 섞으면 안 된다. -->
                            <div v-if="rootType === 'guide_text2'" class="f">
                                <label class="cb">
                                    <input type="checkbox" :checked="!!display.is_toggle" @change="setDisplay('is_toggle', $event.target.checked)">
                                    <span><b>접기 버튼</b> — 눌러서 펼치는 형태로 보여줍니다</span>
                                </label>
                                <label v-if="display.is_toggle" class="cb sub">
                                    <input type="checkbox" :checked="!!display.toggled" @change="setDisplay('toggled', $event.target.checked)">
                                    <span><b>기본으로 접어두기</b></span>
                                </label>
                                <div v-if="display.is_toggle" class="sub">
                                    <label>접기 버튼 제목</label>
                                    <input v-for="t in TOGGLE_TITLE" :key="t.k" type="text" class="mb2"
                                           :value="toggleTitle[t.k] || ''" :placeholder="t.label"
                                           @input="setToggleTitle(t.k, $event.target.value)">
                                </div>
                                <label class="cb">
                                    <input type="checkbox" :checked="!!display.html" @change="setDisplay('html', $event.target.checked)">
                                    <span><b>HTML 로 렌더링</b></span>
                                </label>
                                <label class="cb">
                                    <input type="checkbox" :checked="!!display.bordered" @change="setDisplay('bordered', $event.target.checked)">
                                    <span><b>테두리 상자</b> 안에 넣기</span>
                                </label>
                                <div class="sub">
                                    <label>위·아래 여백 (px)</label>
                                    <div class="opts">
                                        <input type="text" class="mini" :value="display.margin_top == null ? '' : display.margin_top"
                                               placeholder="위 (예: -20)" @input="setDisplay('margin_top', $event.target.value)">
                                        <input type="text" class="mini" :value="display.margin_bottom == null ? '' : display.margin_bottom"
                                               placeholder="아래" @input="setDisplay('margin_bottom', $event.target.value)">
                                    </div>
                                    <p class="h">비우면 기본값(위 0, 아래 3rem)으로 보입니다.</p>
                                </div>
                            </div>

                            <div class="f">
                                <label>제출 차단 (미답변이면 제출 막기)</label>
                                <div class="opts">
                                    <span v-for="o in SUBMIT_REQUIRED" :key="o.v" class="opt" :class="{on: (header.submit_required || 'auto') === o.v}"
                                          @click="setHeader('submit_required', o.v)">{{ o.label }}</span>
                                </div>
                                <p class="h">「캠페인 설정 따름」이면 캠페인의 미답변 제출 차단 옵션을 그대로 씁니다. 켬/끔은 이 문항만 덮어씁니다.</p>
                            </div>
                        </div>

                        <p v-if="!selNode.root" class="h backroot">
                            문항 전체 설정(제목·질문 문구·해당없음·첨부 등)은
                            <button class="lnk" @click="selId = root.id">맨 위 노드</button>를 고르면 나옵니다.
                        </p>

                        <p class="group-hd solo">{{ selNode.type === 'cell' || inCell ? '이 칸' : '이 노드' }}</p>

                        <div class="f">
                            <label>유형</label>
                            <!-- optgroup 이 늦게 그려지면 select 의 :value 가 안 맞는다. option 마다 :selected 로 고정. -->
                            <!-- 루트 유형은 곧 sub_type 인데 changeType 은 트리만 바꿔 저장되지 않는다(silent no-op).
                                 유형 변경은 contents 를 초기화하는 구 편집기가 맡는다. -->
                            <select :disabled="selNode.root" @change="changeType($event.target.value)">
                                <optgroup v-if="inCell" label="칸에 넣을 수 있는 것">
                                    <option v-for="t in cellOkTypes" :key="t" :value="t"
                                            :selected="t === selNode.type">{{ t }}({{ ko(t) }})</option>
                                </optgroup>
                                <optgroup v-if="inCell" label="칸에는 넣을 수 없는 것">
                                    <option v-for="t in cellNoTypes" :key="t" :value="t" disabled
                                            :selected="t === selNode.type">
                                        {{ t }}({{ ko(t) }}) — {{ whyNot(t) }}
                                    </option>
                                </optgroup>
                                <template v-if="!inCell">
                                    <option v-for="t in allTypes" :key="t" :value="t"
                                            :selected="t === selNode.type">{{ t }}({{ ko(t) }})</option>
                                </template>
                            </select>
                            <p v-if="inCell" class="h">칸의 동작은 안에 무엇이 들었는지로만 정해집니다.</p>
                            <p v-else-if="selNode.root" class="h">루트 노드의 유형이 문항 유형(sub_type)입니다. 문항 유형 변경은 구 편집기에서 하세요.</p>
                        </div>

                        <div v-if="selNode.type !== 'row' && selNode.type !== 'cell' && !rootNameField" class="f">
                            <label>{{ fieldLabel }}</label>
                            <textarea :value="rootNameField ? subName : selNode.label"
                                      @input="rootNameField ? setRootName($event.target.value) : setField('label', $event.target.value)"></textarea>
                            <p v-if="rootNameField" class="h">목록과 응답자 화면에 나오는 문항 제목입니다.</p>
                            <p v-else-if="selNode.type === 'column'" class="h">
                                이름을 바꿔도 아래 칸 데이터는 그대로입니다. 열은 이름이 아니라 자기 id 로 연결됩니다.
                            </p>
                        </div>

                        <div v-if="selNode.type === 'calc'" class="f">
                            <label>계산식</label>
                            <input type="text" :value="selNode.formula || ''" placeholder="예: (b1+c1+d1)/3"
                                   :class="{bad: !calcOk.ok}"
                                   @input="setField('formula', $event.target.value)">
                            <p v-if="!calcOk.ok" class="h err">{{ calcOk.msg }} — 이대로 저장하면 응답자 화면에서 계산이 되지 않습니다.</p>
                            <p v-else class="h">같은 행의 다른 칸 이름과 숫자로 씁니다. 사칙연산과 괄호를 쓸 수 있습니다.</p>
                        </div>

                        <div v-if="selNode.type === 'table' && !selNode.root" class="f">
                            <label>표 설명</label>
                            <textarea :value="selNode.description || ''" rows="2" placeholder="표 위에 덧붙는 설명 (비움)"
                                      @input="setField('description', $event.target.value)"></textarea>
                        </div>

                        <div v-if="hasCellValue" class="f">
                            <label>미리 채울 값</label>
                            <input type="text" :value="selNode.value || ''" placeholder="비움"
                                   @input="setField('value', $event.target.value)">
                            <p class="h">응답자 화면에 이 값이 <b>미리 들어간 채로</b> 뜹니다. 비우면 빈 칸으로 시작합니다.</p>
                        </div>

                        <div v-if="selNode.type === 'input_text'" class="f">
                            <label>입력 형식</label>
                            <div class="opts">
                                <span v-for="o in CELL_INPUT_TYPE" :key="o.v" class="opt"
                                      :class="{on: (selNode.srcInputType === 'date' ? 'date' : 'text') === o.v}"
                                      @click="setField('srcInputType', o.v === 'date' ? 'date' : undefined)">{{ o.label }}</span>
                            </div>
                        </div>

                        <div v-if="isElementInput" class="f">
                            <label>입력폼 제목</label>
                            <input type="text" :value="selNode.formTitle || ''" placeholder="입력칸 위에 붙는 제목 (비움)"
                                   @input="setField('formTitle', $event.target.value)">
                        </div>
                        <div v-if="isElementInput" class="f">
                            <label>뒤쪽 문구</label>
                            <input type="text" :value="selNode.afterText || ''" placeholder="(예) 명, tCO2e"
                                   @input="setField('afterText', $event.target.value)">
                            <p class="h">입력칸 오른쪽에 붙는 단위나 말입니다.</p>
                        </div>

                        <div v-if="hasPlaceholder" class="f">
                            <label>입력 안내</label>
                            <input type="text" :value="selNode.placeholder || ''" @input="setField('placeholder', $event.target.value)">
                        </div>

                        <div v-if="canRequire" class="f">
                            <label>필수 셀</label>
                            <label class="cb">
                                <input type="checkbox" :checked="!!selNode.required"
                                       @change="setRequired($event.target.checked)">
                                <span>이 칸을 비우면 제출이 막힙니다</span>
                            </label>
                            <p class="h">지정하면 문항의 제출 차단이 「켬」으로 올라갑니다 (명시적 「끔」은 그대로 둡니다).</p>
                        </div>

                        <div v-if="isChoiceParent" class="f">
                            <label>선택지</label>
                            <div v-for="(ch, i) in selNode.children" :key="ch.id" class="choice-row">
                                <input type="text" :value="ch.label" @input="setChoice(i, $event.target.value)">
                                <input v-if="scoringAuto" type="number" step="0.5" class="sc" title="이 선택지의 배점"
                                       :value="ch.score == null ? '' : ch.score" placeholder="점"
                                       @input="setChoiceField(i, 'score', $event.target.value === '' ? '' : Number($event.target.value))">
                                <!-- 저장 키. 문구와 다를 때만 보여준다 — 채점식이 가리키는 값이다. -->
                                <code v-if="ch.value !== undefined && ch.value !== ch.label"
                                      class="skey" :title="'저장 키 — 채점식과 이미 제출된 답변이 이 값을 씁니다'">{{ ch.value }}</code>
                                <button class="btn tiny bad" @click="removeChoice(i)">×</button>
                            </div>
                            <div v-for="(ch, i) in selNode.children" :key="'d' + ch.id" class="detail-row">
                                <label class="cb">
                                    <input type="checkbox" :checked="!!ch.detail" @change="setChoiceField(i, 'detail', $event.target.checked)">
                                    <span>{{ i + 1 }}번 선택지에 <b>상세 입력 칸</b></span>
                                </label>
                                <template v-if="ch.detail">
                                    <input type="text" class="dp" :value="ch.detailPlaceholder || ''" placeholder="입력 안내 문구"
                                           @input="setChoiceField(i, 'detailPlaceholder', $event.target.value)">
                                    <span class="opts">
                                        <span v-for="o in DETAIL_TYPE" :key="o.v" class="opt" :class="{on: (ch.detailType || 'text') === o.v}"
                                              @click="setChoiceField(i, 'detailType', o.v)">{{ o.label }}</span>
                                    </span>
                                </template>
                            </div>
                            <button class="btn tiny" @click="addChoice">+ 선택지</button>
                            <p class="h">문구를 고쳐도 <b>저장 키는 바뀌지 않습니다</b>. 채점식과 이미 제출된
                                답변이 그 값을 쓰기 때문입니다. 새로 만든 선택지는 문구가 곧 저장 키입니다.</p>
                        </div>

                        <div v-if="selNode.type === 'row'" class="f">
                            <label>행</label>
                            <button class="btn tiny" @click="dupRow">이 행 복제</button>
                            <button class="btn tiny bad" @click="delRow">이 행 삭제</button>
                            <p class="h">복제하면 칸 유형·드롭다운 선택지·입력 안내가 그대로 따라옵니다.</p>
                        </div>

                        <div v-if="selNode.orphanKeys && selNode.orphanKeys.length" class="warnbox">
                            지금 열에 없는 값이 {{ selNode.orphanKeys.length }}개 남아 있습니다 —
                            <span class="mono">{{ selNode.orphanKeys.join(' · ') }}</span>
                            <button class="btn tiny" @click="setField('orphanKeys', [])">정리</button>
                        </div>

                        <div class="acts">
                            <button class="btn tiny" :disabled="!!noChild" :title="noChild"
                                    @click="addChild">+ 자식</button>
                            <button class="btn tiny" :disabled="!!noSibling" :title="noSibling"
                                    @click="addSibling">+ 형제</button>
                            <button class="btn tiny bad" @click="removeNode">삭제</button>
                        </div>
                        <p v-if="noChild || noSibling" class="h" style="margin-top:.35rem">
                            {{ noChild || noSibling }}
                        </p>
                    </template>
                </div>
            </div>
        </div>

        <!-- 저장 payload 미리보기 (Draft) -->
        <details v-if="root" class="sv2-payload">
            <summary>저장 바디 보기 — POST /subs/init (구조·문항 설정·배점)</summary>
            <pre>{{ payloadPreview }}</pre>
        </details>
    </div>
</template>

<script>
import { getErrorMessage } from '@core/utils/api/errorMessage';
import axios from '@core_utils/api/_axios.js';
import Sv2Node from '@/components/structureV2/Sv2Node.vue';
import Sv2Score from '@/components/structureV2/Sv2Score.vue';
import Sv2I18n from '@/components/structureV2/Sv2I18n.vue';
import {targetsOf, refPathsOf, EV_CODES_DEFAULT} from '@core_utils/subComponent/scoring-formula.js';
// 미리보기는 응답자가 실제로 보는 그 컴포넌트를 그대로 쓴다.
// 다시 구현하면 렌더러가 둘이 되고 반드시 갈라진다(_v2 포크가 그랬다).
// props 조합은 EditingScoringMethod_v2 의 '문항 미리보기' 와 동일하다.
import SubComponent from '@core_components/client/components/subs/SubComponent.vue';
import {
    fromV1, toV1, makeNode, walk, findById, countNodes, maxDepth,
    nodeMeta, cellAccepts, whyNotInCell, CHOICE_PARENTS, NODE_META,
    ensureTableShape, addColumn, removeColumn, moveColumn, addRow, removeRow, duplicateRow,
    validateCalc, calcProblems,
    whyNoChild, whyNoSibling, REQUIRABLE_CELL
} from '@core_utils/subComponent/structure-tree.js';
// 제출 차단 승격 규칙은 v1 편집기(EditingDFTable)와 같은 모듈을 쓴다 — 규칙이 갈리면 안 된다
const {promoteSubmitRequiredForCell} = require('@core/mixins/submit-required-override.cjs');


/**
 * 문항 편집기 v2 본체. sub_code 를 받아 원본을 직접 GET 한다 — store 의 question_subs 는
 * generateSubcomponentDataBy 를 거친 손상본(표 번역 사이드카 제거, 배점 필드 누락)이라 쓰지 않는다.
 *   props  code: 문항 코드 / demo: {sub, evals} 데모 픽스처(초안 페이지 전용, 저장 차단)
 *   emits  saved(part) 저장 성공 + 자체 재조회 뒤 / dirty(bool) 저장 안 한 변경 유무 / cancel() 닫기
 */
export default {
    name: 'Sv2Editor',
    components: {Sv2Node, Sv2Score, Sv2I18n, SubComponent},
    props: {
        code: {type: String, default: ''},
        demo: {type: Object, default: null}
    },
    data() {
        return {
            root: null, selId: null, notes: [], loadedSub: null, flash: '', evalsLoaded: false, evalsCode: '',
            subCode: '', subName: '',
            mode: 'preview', dirty: 0, scoreDirty: false, i18nDirty: false,
            loading: false, loadError: '', saveFailed: false, savedAt: '',
            // ── 채점 ──  (평가요소 목록 자체는 Sv2Score 가 들고 있고, 여기는 구조 클릭을 넘겨주는 배선만)
            tab: 'edit', condMarks: {}, pickMode: null,
            evals: [], evalsError: '', evCodes: EV_CODES_DEFAULT.slice(),
            // ── 번역 ──
            i18nMissing: 0,
            SUBMIT_REQUIRED: [{v: 'auto', label: '캠페인 설정 따름'}, {v: 'on', label: '켬'}, {v: 'off', label: '끔'}],
            SCORING_MODE: [{v: 'none', label: '평가요소로 채점'}, {v: 'auto', label: '선택지별 배점'}],
            CELL_INPUT_TYPE: [{v: 'text', label: '글자'}, {v: 'date', label: '날짜'}],
            DETAIL_TYPE: [{v: 'text', label: '글자'}, {v: 'number', label: '숫자'}],
            TOGGLE_TITLE: [{k: 'ko', label: '국문 버튼 제목'}, {k: 'ea', label: '영문 버튼 제목'}, {k: 'ca', label: '중문 버튼 제목'}]
        };
    },
    computed: {
        allTypes() { return Object.keys(NODE_META).filter(t => nodeMeta(t).kind === 'content'); },
        cellOkTypes() { return Object.keys(NODE_META).filter(cellAccepts); },
        cellNoTypes() { return Object.keys(NODE_META).filter(t => !cellAccepts(t)); },
        nodeCount() { return this.root ? countNodes(this.root) : 0; },
        depth() { return this.root ? maxDepth(this.root) : 0; },
        selHit() { return this.root && this.selId ? findById(this.root, this.selId) : null; },

        // ── 채점 ──
        targets() { return this.root ? targetsOf(this.root) : []; },
        refPaths() { return this.root ? refPathsOf(this.root) : []; },
        subMeta() {
            const sub = this.loadedSub || {};
            return {calc_method: sub.calc_method == null ? null : sub.calc_method,
                cap_score: sub.cap_score == null ? null : sub.cap_score,
                score_scaler: sub.score_scaler == null ? 1 : sub.score_scaler};
        },
        selNode() { return this.selHit ? this.selHit.node : null; },
        inCell() { return !!(this.selHit && this.selHit.parent && this.selHit.parent.type === 'cell'); },
        isChoiceParent() { return !!this.selNode && CHOICE_PARENTS.indexOf(this.selNode.type) >= 0; },
        hasPlaceholder() { return !!this.selNode && ['input_text', 'input_number', 'textbox'].indexOf(this.selNode.type) >= 0; },
        scoringAuto() { return !!(this.header && this.header.scoring === 'auto'); },
        /** 선택지 안에 들어간 입력 요소인가 (루트 입력과 계약이 다르다) */
        isElementInput() {
            return !!this.selNode && !this.selNode.root && !this.inCell &&
                ['input_text', 'input_number'].indexOf(this.selNode.type) >= 0;
        },
        calcOk() { return this.selNode && this.selNode.type === 'calc' ? validateCalc(this.selNode.formula) : {ok: true, msg: ''}; },
        /** 지금 표에서 필수로 지정된 칸 */
        tableRequiredCells() {
            const t = this.ownerTable;
            if (!t) return [];
            const out = [];
            (t.children || []).forEach((row) => (row.children || []).forEach((cell) => {
                const c = cell.children && cell.children[0];
                if (c && c.required) out.push(c);
            }));
            return out;
        },
        submitOff() { return this.header && this.header.submit_required === 'off'; },
        /** 미리 채울 값은 표 칸에서만 뜻이 있다 (행의 그 열 값) */
        hasCellValue() {
            return this.inCell && !!this.selNode &&
                ['input_text', 'input_number', 'dropdown'].indexOf(this.selNode.type) >= 0;
        },
        /** 필수 지정은 표의 칸에서만 뜻이 있다 — 저장 자리가 그 행의 required_cells 다 */
        canRequire() {
            return this.inCell && !!this.selNode && REQUIRABLE_CELL.indexOf(this.selNode.type) >= 0;
        },
        /** guide_text2 의 표시 옵션 — 루트 노드에 실려 있고 toV1 이 문항 내용으로 되돌린다 */
        display() { return (this.root && this.root.display) || {}; },
        toggleTitle() { return (this.root && this.root.toggleTitle) || {}; },
        /** 문항 유형 — 표시 방식 컨트롤을 유형별로 가른다 */
        rootType() { return this.root ? (this.root.subType || this.root.type) : ''; },
        /** 태그는 배열로 오간다. 화면에서는 쉼표로 이어 보여준다(구 편집기와 같은 방식). */
        tagsText() {
            const t = this.loadedSub && this.loadedSub.tags;
            return Array.isArray(t) ? t.join(',') : (t || '');
        },
        /** 루트의 label 이 곧 sub_name 인 유형. 안내문은 label 이 본문이라 뺀다. */
        rootNameField() {
            return !!this.selNode && !!this.selNode.root &&
                ['guide_text', 'guide_text2'].indexOf(this.selNode.type) < 0;
        },
        fieldLabel() {
            if (this.rootNameField) return '문항 제목';
            if (!this.selNode) return '내용';
            if (this.selNode.type === 'column') return '열 이름';
            if (this.selNode.root) return '안내문 본문';
            if (this.selNode.type === 'table') return '표 제목';
            return '내용';
        },
        ownerTable() {
            if (!this.root || !this.selId) return null;
            let hit = null;
            walk(this.root, (n) => {
                if (n.type !== 'table') return;
                walk(n, (d) => { if (d.id === this.selId) hit = n; });
            });
            return hit;
        },
        selColIndex() {
            if (!this.ownerTable) return -1;
            return (this.ownerTable.columns || []).findIndex((c) => c.id === this.selId);
        },
        noChild() { return whyNoChild(this.selNode, this.selHit && this.selHit.parent); },
        noSibling() { return whyNoSibling(this.selNode, this.selHit && this.selHit.parent); },
        crumb() {
            if (!this.selHit) return '';
            return this.selNode.root ? 'root · 이 유형이 문항 유형(sub_type)' : 'root › ' + this.selHit.path.join(' › ');
        },
        /** 구조·채점·번역 어느 하나라도 저장 안 한 변경이 있는가 — 부모가 모달 닫힘을 막는 데 쓴다 */
        anyDirty() { return this.dirty > 0 || this.scoreDirty || this.i18nDirty; },
        stateClass() {
            if (this.saveFailed) return 'failed';
            if (this.dirty || this.scoreDirty) return 'dirty';
            return this.savedAt ? 'saved' : '';
        },
        stateText() {
            if (this.saveFailed) return '저장 실패';
            // 채점 탭의 배점·평가요소 변경은 건수를 세지 않는다 — 1건으로 친다
            if (this.dirty || this.scoreDirty) return `저장하지 않은 변경 ${this.dirty + (this.scoreDirty ? 1 : 0)}건`;
            return this.savedAt ? `저장됨 · ${this.savedAt}` : '변경 없음';
        },
        previewKey() { return `${this.subCode}-${this.dirty}`; },
        previewSub() {
            if (!this.root) return null;
            try {
                return {...(this.loadedSub || {}), sub_code: this.subCode, sub_type: this.root.subType,
                    sub_data_contents: toV1(this.root)};
            } catch (e) {
                console.error('[Sv2Editor] 미리보기 변환 실패', e);
                return null;
            }
        },
        /** 문항 설정 — loadedSub.sub_data_header 를 직접 가리킨다 (없으면 만든다) */
        header() {
            if (!this.loadedSub) return null;
            if (!this.loadedSub.sub_data_header || typeof this.loadedSub.sub_data_header !== 'object') this.$set(this.loadedSub, 'sub_data_header', {});
            return this.loadedSub.sub_data_header;
        },
        /** POST /subs/init 에 나갈 바디 — 백엔드가 받는 필드만. 배점 셋은 새 API(부분 갱신) */
        savePayload() {
            if (!this.root) return null;
            const sub = this.loadedSub || {};
            const out = {
                sub_code: this.subCode, label: sub.label, sub_type: this.root.subType,
                sub_name: this.subName || null, sub_description: sub.sub_description, sub_info: sub.sub_info,   // DB 의 null 을 '' 로 바꿔 보내지 않는다
                sub_condition: sub.sub_condition, sub_priority: sub.sub_priority, sub_parent: sub.sub_parent, sub_child: sub.sub_child,
                tags: sub.tags || [], risk: !!sub.risk,
                sub_data_header: this.header || {},
                sub_data_contents: toV1(this.root)
            };
            ['calc_method', 'cap_score', 'score_scaler'].forEach((k) => { if (sub[k] !== undefined) out[k] = sub[k]; });
            return out;
        },
        payloadPreview() {
            return this.savePayload ? JSON.stringify(this.savePayload, null, 2).slice(0, 4000) : '';
        }
    },
    watch: {
        code: {immediate: true, handler(c) { if (c) this.load(c); }},
        demo: {immediate: true, handler(d) { if (d) this.useDemo(); }},
        anyDirty(v) { this.$emit('dirty', v); }
    },
    created() { this.loadEvCodes(); },
    methods: {
        ko(t) { return nodeMeta(t).ko; },
        whyNot(t) { return whyNotInCell(t); },
        bump() { this.dirty += 1; this.saveFailed = false; },

        useDemo() { this.apply(this.demo.sub); this.loadError = ''; this.evals = (this.demo.evals || []).slice(); this.evalsError = ''; this.evalsLoaded = true; },

        async load(code = this.subCode || this.code) {
            code = (code || '').trim();
            if (!code) return;
            this.loading = true; this.loadError = '';
            try {
                const params = new URLSearchParams();
                params.append('sub_codes', code);
                const res = await axios.get('/subs/init', {params});
                const sub = (res.data || [])[0];
                if (!sub) throw new Error(`문항 ${code} 을(를) 찾지 못했습니다.`);
                this.apply(sub);
                this.loadEvals(code);
            } catch (e) {
                // 실패를 삼키지 않는다 — 이 화면이 고치려는 문제가 그거다
                console.error('[Sv2Editor] 문항 불러오기 실패', {code, error: e});
                this.loadError = e.message || '불러오지 못했습니다.';
            } finally {
                this.loading = false;
            }
        },

        apply(sub) {
            try {
                const {root, notes} = fromV1(sub);
                this.root = root;
                // 깊은 복사 — header/번역 탭이 loadedSub 를 직접 고치므로 원본을 공유하면 되돌리기가 안 된다
                this.loadedSub = JSON.parse(JSON.stringify(sub));
                this.i18nDirty = false;
                this.notes = notes;
                this.selId = root.id;
                this.subCode = sub.sub_code || '';
                this.subName = sub.sub_name || '';
                this.dirty = 0; this.savedAt = ''; this.saveFailed = false;
            } catch (e) {
                console.error('[Sv2Editor] 변환 실패', {sub, error: e});
                this.loadError = '이 문항은 아직 변환할 수 없습니다: ' + e.message;
            }
        },

        reload() { this.isDemo() ? this.useDemo() : this.load(); },
        /** 되돌리기는 구조·설정·번역·채점 변경을 모두 버린다 — 건수를 보여주고 확인받는다 */
        async confirmReload() {
            const n = this.dirty + (this.scoreDirty ? 1 : 0) + (this.i18nDirty ? 1 : 0);
            const ok = await this.$showModal({type: 'confirm', state: 'warning', title: '변경 내용 버리기',
                message: `저장하지 않은 변경 ${n}건(구조·설정·번역·채점 모두)을 버리고 서버에 저장된 값으로 되돌립니다. 되돌릴 수 없습니다.`});
            if (ok) this.reload();
        },

        /** 데모 문항은 어디에도 보내지 않는다 — 실제 문항을 불러온 뒤에만 저장이 나간다 */
        isDemo() { return !this.subCode || this.subCode.startsWith('DEMO'); },
        stamp() { const d = new Date(); return `${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')}`; },
        async save() {
            if (this.isDemo()) { this.flash = '데모 문항은 저장하지 않습니다. 실제 문항을 불러오세요.'; return; }
            // 깨진 계산식은 저장은 되는데 응답자 화면에서 조용히 계산이 안 된다 — v1 도 막았다
            const bad = this.root ? calcProblems(this.root) : [];
            if (bad.length) {
                this.selId = bad[0].id;
                this.flash = `계산식 ${bad.length}곳을 먼저 고쳐 주세요 — ${bad[0].msg}`;
                return;
            }
            // 배점만 바꾸면 상단 '저장' 이 죽어 있었다(2026-09-17 C4-1.1v2 cap_score). 채점 변경이 있으면
            // 채점 패널의 save 한 경로로 보낸다 — saveScore 가 구조 변경까지 같은 /subs/init 으로 함께 저장한다.
            // 여기서 구조 저장을 따로 또 쏘면 loadedSub 의 옛 배점이 뒤늦게 덮어쓸 수 있다.
            if (this.scoreDirty && this.$refs.score) { this.$refs.score.save(); return; }
            const payload = this.savePayload;
            console.info('[Sv2Editor] POST /subs/init', payload);
            try {
                // 구 편집기(EditingSubComponentMixin.saveSub)와 같은 엔드포인트·바디. 백엔드가 화이트리스트로 병합한다.
                await axios.post('/subs/init', payload);
                // 서버가 번역·병합한 결과를 다시 읽어 화면과 DB 를 맞춘다. 목록 갱신은 부모 몫.
                // apply() 가 savedAt 을 비우므로 재조회 뒤에 찍어야 '저장됨' 이 보인다.
                await this.load();
                // 이전 실패 문구를 지운다 — 남아 있으면 성공한 저장을 실패로 오인한다
                this.dirty = 0; this.saveFailed = false; this.flash = '';
                this.savedAt = this.stamp();   // stateText 가 "저장됨 · " 를 붙인다
                this.$emit('saved', 'structure');
            } catch (e) {
                // 실패를 삼키지 않는다 — 이 화면이 고치려는 문제가 그거다
                console.error('[Sv2Editor] 저장 실패', {payload, error: e});
                this.saveFailed = true;
                this.flash = '저장 실패: ' + (getErrorMessage(e, '') || (e.response && ((e.response.data && e.response.data.description) || e.response.status)) || e.message || '오류');
            }
        },

        // ── 채점 ──
        async loadEvals(code) {
            this.evalsError = '';
            // 다른 문항이면 이전 문항의 평가요소가 보이지 않게 비운다. 같은 문항 재조회(저장 후)는 패널을 유지한다.
            if (this.evalsCode !== code) { this.evals = []; this.evalsLoaded = false; this.evalsCode = code; }
            try {
                // 실제 채점 편집기(EditingScoringMethod_v2)와 같은 API. 실패해도 문항 편집은 계속된다.
                const res = await axios.get(`/evaluation/${encodeURIComponent(code)}`);
                // 백엔드 SubEvaluationHandler.serialized_evaluations 는 {sub_code: [...]} 딕셔너리다.
                // 배열이나 {evaluations: [...]} 로 바뀌어도 받도록 세 모양을 다 본다.
                const d = res.data;
                this.evals = Array.isArray(d) ? d
                    : (d && Array.isArray(d.evaluations)) ? d.evaluations
                    : (d && Array.isArray(d[code])) ? d[code]
                    : (d && typeof d === 'object') ? Object.values(d).filter(Array.isArray).flat() : [];
                this.evalsLoaded = true;
            } catch (e) {
                this.evalsLoaded = false;
                console.error('[Sv2Editor] 수식 불러오기 실패', {code, error: e});
                this.evalsError = '저장된 평가요소를 불러오지 못했습니다 (' + (e.message || '오류') + ')';
            }
        },
        async loadEvCodes() {
            try {
                const res = await axios.get('/evaluation/codes');
                const list = Array.isArray(res.data) ? res.data : (res.data && res.data.codes) || [];
                if (list.length) this.evCodes = list.map((x) => ({code: x.code, level: x.level, description: x.description || ''}));
            } catch (e) {
                // 백엔드가 없으면 실측 기본표를 쓴다 — 화면은 계속 동작한다
            }
        },
        /** 구조 클릭: 채점 탭이면 평가요소 목록이 처리(대상 또는 증빙 위치), 아니면 편집 선택 */
        onPick(id) {
            if (this.tab === 'score') {
                const sc = this.$refs.score;
                if (sc && sc.onPick(id)) return;
                this.flash = '오른쪽에서 「대상 고르기」나 「증빙 위치 고르기」를 먼저 누르세요.';
                return;
            }
            this.selId = id;
        },
        // ── 번역 ──
        async saveI18n({meta, contents}) {
            if (this.isDemo()) { this.flash = '데모 문항은 저장하지 않습니다. 실제 문항을 불러오세요.'; return; }
            console.info('[Sv2Editor] POST /translate/subs_init/' + this.subCode, {meta, contents});
            const url = `/translate/subs_init/${encodeURIComponent(this.subCode)}`;
            const manual = (part) => ({params: {init_translation: false, partial_translation: true, translation_part: part}});
            try {
                // 구 화면(EditingTranslation.vue:1411/1453)과 같은 두 호출. 배열은 배열로 나간다.
                await axios.post(url, {sub: meta}, manual('meta'));
                await axios.post(url, {sub: {sub_code: this.subCode, sub_data_contents: contents}}, manual('sub_data_contents'));
                this.flash = `${this.stamp()} 번역 저장됨`;
                this.saveFailed = false;
                await this.load(); this.$emit('saved', 'i18n');
                this.savedAt = this.stamp();
            } catch (e) {
                console.error('[Sv2Editor] 번역 저장 실패', {error: e});
                this.saveFailed = true;
                this.flash = '번역 저장 실패: ' + (getErrorMessage(e, '') || (e.response && ((e.response.data && e.response.data.description) || e.response.status)) || e.message || '오류');
            }
        },
        async autoTranslate(part) {
            // 자동 번역은 서버가 하고 결과를 DB 에 쓴다 — 구 화면(EditingTranslation.vue:1342)과 같은 호출.
            if (this.isDemo()) { this.flash = '데모 문항은 자동 번역을 부를 수 없습니다. 실제 문항을 불러오세요.'; return; }
            try {
                const body = part === 'meta'
                    ? {sub: {sub_code: this.subCode, sub_name: this.loadedSub.sub_name, sub_description: this.loadedSub.sub_description, sub_info: this.loadedSub.sub_info, sub_data_header: this.loadedSub.sub_data_header}}
                    : {sub: {sub_code: this.subCode, sub_data_contents: toV1(this.root)}};
                await axios.post(`/translate/subs_init/${encodeURIComponent(this.subCode)}`, body,
                    {params: {init_translation: true, partial_translation: true, translation_part: part}});
                this.flash = '자동 번역을 요청했습니다. 문항을 다시 불러옵니다.';
                this.saveFailed = false;
                await this.load(); this.$emit('saved', 'i18n');
            } catch (e) {
                console.error('[Sv2Editor] 자동 번역 실패', {part, error: e});
                this.saveFailed = true;
                this.flash = '자동 번역에 실패했습니다 (' + (e.message || '오류') + ')';
            }
        },
        async saveScore({evaluations, meta}) {
            if (this.isDemo()) { this.flash = '데모 문항은 저장하지 않습니다. 실제 문항을 불러오세요.'; return; }
            console.info('[Sv2Editor] POST /evaluation/' + this.subCode, {evaluations, meta});
            try {
                // 평가요소 배열은 실제 채점 편집기(EditingScoringMethod_v2)와 같은 API·계약(SubEvaluationHandler)
                await axios.post(`/evaluation/${encodeURIComponent(this.subCode)}`, {evaluations});
                // 문항 배점 셋은 문항 저장 경로(부분 갱신) — 값이 바뀐 것만. 상단 '저장' 에서 넘어온 구조 변경(dirty)도 여기서 같이 보낸다.
                const cur = this.loadedSub || {};
                const changed = {};
                ['calc_method', 'cap_score', 'score_scaler'].forEach((k) => { if ((cur[k] == null ? null : cur[k]) !== (meta[k] == null ? null : meta[k])) changed[k] = meta[k]; });
                const saveSub = Object.keys(changed).length > 0 || this.dirty > 0;
                if (saveSub) await axios.post('/subs/init', {...this.savePayload, ...changed});
                this.flash = `${this.stamp()} 평가요소 ${evaluations.length}개 저장됨` + (Object.keys(changed).length ? ' · 문항 배점 갱신' : '') + (this.dirty ? ' · 구조 저장' : '');
                await this.loadEvals(this.subCode);
                if (saveSub) await this.load();   // apply() 가 dirty 를 0 으로 되돌린다
                this.saveFailed = false;
                this.savedAt = this.stamp();
                this.$emit('saved', 'score');
            } catch (e) {
                console.error('[Sv2Editor] 채점 저장 실패', {evaluations, error: e});
                this.saveFailed = true;
                this.flash = '채점 저장 실패: ' + (getErrorMessage(e, '') || (e.response && ((e.response.data && e.response.data.description) || e.response.status)) || e.message || '오류');
            }
        },

        setField(key, val) { this.$set(this.selNode, key, val); this.bump(); },
        /** 문항 제목은 트리가 아니라 sub_name 이다. 저장바·미리보기·캔버스가 같이 따라오게 셋 다 맞춘다. */
        /** 문항 자체의 속성(df_subs_init 컬럼). savePayload 가 loadedSub 에서 읽어 가므로 여기만 쓰면 된다. */
        setSubField(key, val) {
            if (!this.loadedSub) return;
            this.$set(this.loadedSub, key, val);
            this.bump();
        },
        /** 값을 다듬지 않는다 — 태그 마스터에 선행 공백이 섞인 이름이 있어 trim 하면 기존 태그와 매칭되지 않는다.
         *  빈 입력은 빈 배열이고, 백엔드는 빈 배열이면 태그를 건드리지 않는다(기존 태그 유지). */
        setTags(v) {
            const seen = {};
            const list = String(v || '').split(',').filter((x) => {
                // 같은 이름을 두 번 보내면 백엔드가 연결 테이블 중복으로 500 을 낸다 (staging 실측)
                if (x === '' || seen[x]) return false;
                seen[x] = 1;
                return true;
            });
            this.setSubField('tags', list);
        },
        /** guide_text2 표시 옵션. 루트 노드에 쓰면 toV1 이 문항 내용의 제자리로 돌려놓는다. */
        setDisplay(key, val) {
            if (!this.root) return;
            if (!this.root.display) this.$set(this.root, 'display', {});
            this.$set(this.root.display, key, val);
            // 접기 버튼을 끄면 '기본 접힘' 도 뜻이 없다
            if (key === 'is_toggle' && !val) this.$set(this.root.display, 'toggled', false);
            this.bump();
        },
        setToggleTitle(loc, val) {
            if (!this.root) return;
            if (!this.root.toggleTitle) this.$set(this.root, 'toggleTitle', {});
            this.$set(this.root.toggleTitle, loc, val);
            this.bump();
        },
        setRootName(v) {
            this.subName = v;
            if (this.loadedSub) this.$set(this.loadedSub, 'sub_name', v);
            // 안내문 루트는 label 이 본문이라 건드리면 본문이 날아간다
            if (['guide_text', 'guide_text2'].indexOf(this.root.type) < 0) this.$set(this.root, 'label', v);
            this.bump();
        },
        /** 필수 셀 — v1(EditingDFTable:630-635)과 같이 문항의 제출 차단을 '켬' 으로 승격한다 */
        setRequired(on) {
            this.setField('required', on);
            if (on && this.header) this.$set(this.header, 'submit_required', promoteSubmitRequiredForCell(this.header.submit_required));
        },
        /** 문항 설정 한 칸. 미리보기는 previewSub 가 loadedSub 를 펼쳐 쓰므로 bump 로 다시 그린다. */
        setHeader(key, val) {
            if (!this.header) return;
            if (key === 'enable_na' && !val) { this.$set(this.header, 'enable_na', false); this.bump(); return; }
            if (key === 'enable_na_label') { this.$set(this.header, key, val); this.bump(); return; }
            // 문항 첨부와 선택지별 첨부는 같이 켤 수 없다 — 켜면 응답자 화면에 첨부 칸이 두 개 뜬다.
            // v1 은 구조적으로 막던 조합이라 여기서도 하나를 켜면 다른 하나를 끈다.
            if (key === 'has_attachment' && val) this.$set(this.header, 'required_opt_attachment', false);
            if (key === 'required_opt_attachment' && val) this.$set(this.header, 'has_attachment', false);
            // 첨부가 둘 다 꺼지면 '첨부 필수' 는 뜻이 없다
            if ((key === 'has_attachment' || key === 'required_opt_attachment') && !val) {
                const other = key === 'has_attachment' ? 'required_opt_attachment' : 'has_attachment';
                if (!this.header[other]) this.$set(this.header, 'required_attachment', false);
            }
            this.$set(this.header, key, val); this.bump();
        },
        setChoice(i, val) { this.$set(this.selNode.children[i], 'label', val); this.bump(); },
        setChoiceField(i, key, val) {
            const ch = this.selNode.children[i];
            this.$set(ch, key, val);
            // 상세 입력을 켜면 유형 기본값을 준다 (v1 도 타입을 고르게 했다)
            if (key === 'detail' && val && ch.detailType === undefined) this.$set(ch, 'detailType', 'text');
            this.bump();
        },
        addChoice() { this.selNode.children.push(makeNode('plain_text', {label: `선택지 ${this.selNode.children.length + 1}`})); this.bump(); },
        removeChoice(i) { this.selNode.children.splice(i, 1); this.bump(); },

        // 격자의 '+' 와 인스펙터의 '+ 열 / + 행' 이 같은 경로를 탄다.
        // 격자는 표 id 를 넘기고, 인스펙터는 지금 고른 칸이 속한 표를 쓴다.
        tableOf(tableId) {
            if (!tableId) return this.ownerTable;
            const hit = findById(this.root, tableId);
            return hit ? hit.node : null;
        },
        addColIn(tableId) {
            const t = this.tableOf(tableId);
            if (!t) return;
            this.selId = addColumn(t).id; this.bump();
        },
        addRowIn(tableId) {
            const t = this.tableOf(tableId);
            if (!t) return;
            this.selId = addRow(t).id; this.bump();
        },
        moveColIn({tableId, from, to}) {
            const t = this.tableOf(tableId);
            if (!t) return;
            moveColumn(t, from, to); this.bump();
        },
        setCol(i, v) { this.$set(this.ownerTable.columns[i], 'label', v); this.bump(); },
        delCol(i) {
            const name = this.ownerTable.columns[i].label || '이름 없음';
            if (!confirm(`'${name}' 열과 각 행의 해당 칸을 지웁니다.`)) return;
            removeColumn(this.ownerTable, i); this.bump();
        },
        moveCol(from, to) { moveColumn(this.ownerTable, from, to); this.bump(); },
        dupRow() {
            const table = this.selHit && this.selHit.parent;
            if (!table) return;
            const at = table.children.indexOf(this.selNode);
            const copy = duplicateRow(table, at);
            if (copy) { this.selId = copy.id; this.bump(); }
        },
        delRow() {
            const table = this.selHit && this.selHit.parent;
            if (!table) return;
            const i = table.children.indexOf(this.selNode);
            if (!confirm('이 행을 지웁니다.')) return;
            removeRow(table, i);
            this.selId = table.id; this.bump();
        },

        changeType(t) {
            // 선택지 자리에 블록 노드를 넣으면 v1 이 표현하지 못한다
            // (선택지는 {text, value} 객체이고, 블록은 element_contents 안에만 들어간다).
            // 유형을 바꾸는 대신 그 선택지 '안에' 넣어 준다.
            const parent = this.selHit && this.selHit.parent;
            const inChoiceSlot = parent && CHOICE_PARENTS.indexOf(parent.type) >= 0;
            if (inChoiceSlot && nodeMeta(t).fit === 'block') {
                const child = makeNode(t, {label: ''});
                if (t === 'table') ensureTableShape(child);
                this.selNode.children.push(child);
                this.selId = child.id;
                this.bump();
                this.$nextTick(() => {
                    this.flash = `'${nodeMeta(t).ko}' 은(는) 선택지 자체가 될 수 없어 이 선택지 안에 넣었습니다.`;
                });
                return;
            }
            this.$set(this.selNode, 'type', t);
            if (CHOICE_PARENTS.indexOf(t) >= 0 && !this.selNode.children.length) {
                this.selNode.children.push(makeNode('plain_text', {label: '선택지 1'}));
            }
            // 표로 바꾸면 열·행이 없으면 만들어 준다. 없으면 편집할 손잡이가 아예 없다.
            if (t === 'table') ensureTableShape(this.selNode);
            this.bump();
        },

        addChild() {
            if (this.noChild) return;
            const n = makeNode('plain_text', {label: '새 노드'});
            this.selNode.children.push(n);
            this.selId = n.id; this.bump();
        },
        addSibling() {
            if (this.noSibling) return;
            const p = this.selHit && this.selHit.parent;
            if (!p) { alert('루트에는 형제를 붙일 수 없습니다.'); return; }
            const list = (p.columns || []).indexOf(this.selNode) >= 0 ? p.columns : p.children;
            const n = makeNode(list === p.columns ? 'column' : 'plain_text', {label: '새 노드'});
            list.splice(list.indexOf(this.selNode) + 1, 0, n);
            if (list === p.columns) p.children.forEach(r => r.children.push(makeNode('cell', {children: [makeNode('input_text')]})));
            this.selId = n.id; this.bump();
        },
        removeNode() {
            const p = this.selHit && this.selHit.parent;
            if (!p) { alert('루트는 지울 수 없습니다.'); return; }
            const isCol = (p.columns || []).indexOf(this.selNode) >= 0;
            if (!confirm(isCol ? '이 열과 각 행의 해당 칸을 지웁니다.' : '이 노드와 아래 전부를 지웁니다.')) return;
            if (isCol) {
                const i = p.columns.indexOf(this.selNode);
                p.columns.splice(i, 1);
                p.children.forEach(r => r.children.splice(i, 1));
            } else {
                p.children.splice(p.children.indexOf(this.selNode), 1);
            }
            this.selId = this.root.id; this.bump();
        }
    }
};
</script>

<style scoped>
.sv2{--l:#dde2e9;--l2:#eaeef3;--i2:#525c6b;--i3:#8b95a3;--br:#2f6b8f;--brs:#e6f0f6;
     --ok:#2c6e52;--warn:#8a6212;--warns:#faf0da;--bad:#a33228;--sunk:#f5f7fa;
     padding:0 0 1rem;text-align:left}
.muted{color:var(--i3)} .err{color:var(--bad);font-size:.85rem} .pad{padding:2rem}
.mono{font-family:'IBM Plex Mono',Consolas,monospace}

.sv2-savebar{display:flex;align-items:center;gap:.8rem;flex-wrap:wrap;padding:.6rem .9rem;
  background:#fff;border:1px solid var(--l);border-radius:7px;margin-bottom:.7rem}
.sv2-savebar .code{font-family:Consolas,monospace;font-size:.78rem;color:var(--i3);
  background:var(--sunk);border:1px solid var(--l2);border-radius:4px;padding:.1rem .45rem}
.sv2-savebar .nm{font-weight:600;margin-right:auto}
.state{font-size:.83rem;display:flex;align-items:center;gap:.4rem}
.state.dirty{color:var(--warn)} .state.saved{color:var(--ok)} .state.failed{color:var(--bad);font-weight:500}
.state .dot{width:.45rem;height:.45rem;border-radius:50%;background:currentColor}

.sv2-notes{background:var(--warns);color:var(--warn);border-radius:6px;padding:.55rem .8rem;
  font-size:.83rem;margin-bottom:.8rem}
.sv2-notes ul{margin:.3rem 0 0;padding-left:1.1rem} .sv2-notes li{margin:.1rem 0}

.sv2-split{display:grid;grid-template-columns:minmax(0,0.85fr) minmax(0,1.15fr) 20rem;gap:.8rem;align-items:start}
/* 채점은 구조를 클릭하며 만든다 — 협력사 화면을 접고 구조와 채점 두 기둥만 넓게 쓴다.
   모달로 빼면 구조를 못 클릭하므로 기둥을 넓히는 쪽을 택했다. */
.sv2-split.wide-mode{grid-template-columns:minmax(0,1fr) minmax(0,1.3fr)}
.sv2-split.wide-mode .sv2-preview{display:none}
.sv2-split.wide-mode .insp{position:static}
/* 좁으면(모달 xl 이 1240px 로 접히는 1280 뷰포트 포함) 세 기둥이 안 들어간다 — 협력사 화면을 한 줄 전체로
   올리고 구조·인스펙터만 2열. 그냥 2열로 두면 구조가 21rem 칸에 끼고 인스펙터가 아래로 밀린다. */
@media (max-width:80rem){.sv2-split{grid-template-columns:minmax(0,1fr) 21rem} .sv2-split .sv2-preview{grid-column:1 / -1} .sv2-split.wide-mode{grid-template-columns:minmax(0,1fr) minmax(0,1.2fr)}}
@media (max-width:64rem){.sv2-split{grid-template-columns:1fr}}
.sv2-preview .pv{padding:.9rem 1rem;max-height:34rem;overflow:auto}
.sv2-preview .hd .r{font-weight:400}
.sv2-card{background:#fff;border:1px solid var(--l);border-radius:7px}
.sv2-card .hd{display:flex;align-items:center;gap:.6rem;padding:.5rem .8rem;border-bottom:1px solid var(--l);
  font-size:.8rem;font-weight:600;color:var(--i2)}
.sv2-card .hd .r{margin-left:auto;font-weight:400;color:var(--i3);font-size:.76rem}
.canvas{padding:1rem 1.1rem 1.4rem}
.insp{position:sticky;top:.8rem} .insp .bd{padding:.7rem .8rem;font-size:.86rem}

.seg{display:inline-flex;border:1px solid var(--l);border-radius:5px;overflow:hidden}
.seg button{border:0;background:#fff;font:inherit;font-size:.78rem;color:var(--i2);
  padding:.15rem .55rem;cursor:pointer;border-right:1px solid var(--l)}
.seg button:last-child{border-right:0}
.seg button[aria-pressed="true"]{background:var(--br);color:#fff}

.f{margin-bottom:.75rem}
.f > label{display:block;font-weight:600;font-size:.8rem;margin-bottom:.2rem}
.f input[type=text],.f select,.f textarea{width:100%;font:inherit;font-size:.85rem;padding:.3rem .45rem;
  border:1px solid var(--l);border-radius:5px}
.f textarea{min-height:3.2rem;resize:vertical}
.f .h{font-size:.76rem;color:var(--i3);margin:.2rem 0 0}
label.cb{display:flex;gap:.4rem;align-items:center;font-size:.85rem;font-weight:400}
.choice-row{display:flex;gap:.25rem;margin-bottom:.2rem}
.choice-row .sc{flex:none;width:4.2rem}
.detail-row{display:flex;flex-wrap:wrap;gap:.3rem;align-items:center;margin:.15rem 0}
.detail-row .dp{flex:1;min-width:8rem}
.backroot{background:var(--brs);border-radius:5px;padding:.35rem .55rem;margin:0 0 .6rem}
.backroot .lnk{border:0;background:none;padding:0;font:inherit;color:var(--br);font-weight:600;text-decoration:underline;cursor:pointer}

/* 열은 가로로 있는 것이니 가로로 보여준다. 조작은 hover 할 때만 드러낸다. */
.cols{display:flex;flex-wrap:wrap;gap:.3rem;align-items:center}
.colchip{display:inline-flex;align-items:center;gap:.1rem;background:#fff;
  border:1px solid var(--l);border-radius:5px;padding:.1rem .15rem}
.colchip.sel{border-color:var(--br);box-shadow:0 0 0 1px var(--br)}
.colchip input{border:0;background:none;padding:.1rem .25rem;font-size:.84rem;
  color:var(--ink);min-width:2.5rem;border-radius:3px}
.colchip input:focus{outline:0;background:var(--brs)}
/* 숨길 때 폭까지 접는다. opacity 만 낮추면 자리를 차지해 칩이 뚱뚱해지고 줄바꿈된다. */
.colchip .mv,.colchip .x{border:0;background:none;cursor:pointer;font-size:.8rem;line-height:1;
  color:var(--i3);overflow:hidden;max-width:0;padding:0;opacity:0;
  transition:max-width .12s, opacity .12s, padding .12s}
.colchip:hover .mv,.colchip:hover .x,
.colchip.sel .mv,.colchip.sel .x,
.colchip:focus-within .mv,.colchip:focus-within .x{max-width:1.4rem;padding:0 .18rem;opacity:1}
/* 삭제는 되돌릴 수 없다. 이동 버튼과 붙여두면 오조작이 난다 — 구분선으로 떼어놓는다. */
.colchip .x{color:var(--i3)}
.colchip:hover .x,.colchip.sel .x,.colchip:focus-within .x{
  margin-left:.25rem;padding-left:.3rem;border-left:1px solid var(--l);border-radius:0}
.colchip .x:hover{color:#fff;background:var(--bad);border-radius:3px}
.colchip .mv:disabled{opacity:.2;cursor:not-allowed}
.colchip .mv:hover:not(:disabled){color:var(--br)}
.colchip .x:hover{color:var(--bad)}
.addcol{align-self:center}
.posrow{display:flex;gap:.4rem;align-items:center;margin:.4rem 0 0}
.skey{flex:none;max-width:9rem;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;
  font-family:Consolas,monospace;font-size:.72rem;color:#8a6212;background:#fbf3e2;
  border:1px solid #ecdcb8;border-radius:3px;padding:.1rem .3rem;cursor:help}
.posname{font-size:.8rem;font-weight:600;color:#2f6b8f;margin-right:.1rem}
.group{border:1px solid var(--l);border-radius:6px;background:var(--sunk);
  padding:.6rem .7rem .2rem;margin-bottom:.9rem}
.group .f{margin-bottom:.6rem}
.group-hd{font-size:.75rem;font-weight:700;letter-spacing:.04em;color:var(--i2);
  margin:0 0 .5rem;text-transform:none}
.group-hd span{font-weight:400;color:var(--i3)}
.group-hd.solo{margin:0 0 .5rem;padding-top:.1rem;border-top:1px solid var(--l2);padding-top:.6rem}
.flash{background:#e6f0f6;color:#22536f;border-radius:5px;padding:.4rem .55rem;font-size:.79rem;
  margin:0 0 .6rem;display:flex;gap:.4rem;align-items:flex-start}
.flash .btn{margin-left:auto;flex:none}
.flash.fail{background:#fbeceb;color:#a33228}
.crumb{font-family:Consolas,monospace;font-size:.72rem;color:var(--i3);margin:0 0 .6rem;
  padding-bottom:.45rem;border-bottom:1px solid var(--l2);word-break:break-all}
.acts{display:flex;gap:.3rem;flex-wrap:wrap;padding-top:.6rem;border-top:1px solid var(--l2)}
.warnbox{background:var(--warns);color:var(--warn);border-radius:5px;padding:.45rem .6rem;
  font-size:.8rem;margin-bottom:.7rem}

.btn{border:1px solid var(--l);background:#fff;color:#141921;padding:.25rem .65rem;border-radius:5px;
  font:inherit;font-size:.82rem;cursor:pointer}
.btn:hover{border-color:var(--i3)}
.btn.primary{background:var(--br);border-color:var(--br);color:#fff}
.btn.tiny{padding:.1rem .45rem;font-size:.76rem}
.btn.bad{color:var(--bad)} .btn.retry{border-color:var(--bad);color:var(--bad)}
.btn:disabled{opacity:.45;cursor:not-allowed}

.sv2-payload{margin-top:1rem;font-size:.82rem;color:var(--i2)}
.sv2-payload pre{background:var(--sunk);border:1px solid var(--l);border-radius:6px;
  padding:.7rem;overflow:auto;max-height:22rem;font-size:.75rem}

/* ── 채점 ── */
.pickhint{margin:.5rem .6rem 0;font-size:.82rem;color:#2f6b8f}
.pickhint.evid{color:#8a6212}
.group.hdr{border-left:3px solid #2f6b8f}
.group.hdr .sub{margin:.35rem 0 0 1.55rem}
.group.hdr .cb{display:flex;align-items:flex-start;gap:.45rem;font-size:.86rem;cursor:pointer;margin:.15rem 0}
.group.hdr .cb input{margin-top:.2rem}
.group.hdr .opts{display:flex;gap:.35rem;flex-wrap:wrap}
.group.hdr .opt{border:1px solid #dde2e9;background:#fff;border-radius:999px;padding:.18rem .65rem;font-size:.8rem;cursor:pointer;color:#3d454e}
.group.hdr .opt.on{border-color:#2f6b8f;background:#2f6b8f;color:#fff}
.f input.bad{border-color:var(--bad);background:#fffafa}
.h.err{color:var(--bad)}
.group.hdr input.mini{width:8rem}
.group.hdr input.mb2{margin-bottom:.25rem}
.segcnt{display:inline-block;margin-left:.3rem;font-family:Consolas,monospace;font-size:.66rem;font-weight:700;color:#fff;background:#8a6212;border-radius:999px;padding:0 .4rem;vertical-align:.1em}
.rule{font-size:.74rem;color:#6b747e;background:#f8f9fb;border:1px dashed #dde2e9;border-radius:5px;padding:.35rem .6rem;margin:.5rem 0 0}
.said{font-size:.98rem;line-height:1.7;padding:.75rem .9rem;background:#e6f0f6;border-radius:7px;color:#153f47;min-height:3rem}
.said.empty{color:#6b747e;background:#f8f9fb;font-size:.88rem}
.said >>> .q{font-weight:600}
.said >>> .kw{font-weight:600;color:#1f5f6b}
.said >>> .grp-say{background:#d6e6ec;border-radius:6px;padding:.05rem .45rem;box-decoration-break:clone;-webkit-box-decoration-break:clone}
.said >>> .grp-say .grp-say{background:#c7dbe3}
.code{margin:0;padding:.65rem .8rem;background:#1c2126;color:#d8dee5;border-radius:7px;font-family:Consolas,monospace;
  font-size:.78rem;line-height:1.6;white-space:pre-wrap;overflow-wrap:anywhere;min-height:2.6rem}
.code.empty{color:#7d8794}
.check{display:flex;flex-direction:column;gap:.3rem;font-size:.82rem}
.check div{display:flex;gap:.45rem;align-items:center}
.check .ok{color:#2c6a4a;font-weight:700}
.check .sw{width:.7rem;height:.7rem;border-radius:2px;background:var(--c);flex:none}
.rrow{display:flex;flex-direction:column;gap:.25rem;padding:.55rem 0;border-bottom:1px solid #eaeef3}
.rrow:last-child{border-bottom:0}
.rrow .raw{font-size:.74rem;color:#3d454e;overflow-wrap:anywhere;line-height:1.5}
.rrow .say{font-size:.88rem;color:#153f47}
.rrow .say.manual{color:#3d454e;font-size:.84rem}
.rrow .say >>> .kw{font-weight:600;color:#1f5f6b}
.rrow .say >>> .grp-say{background:#e6f0f6;border-radius:6px;padding:.05rem .4rem;box-decoration-break:clone;-webkit-box-decoration-break:clone}
.rrow .note{display:block;font-size:.76rem;color:#6b747e;margin-top:.1rem}
.badge{display:inline-block;font-family:Consolas,monospace;font-size:.64rem;padding:.06rem .4rem;border-radius:3px;margin-right:.35rem;vertical-align:.1em;font-weight:600}
.badge.gen{background:#e6f0f6;color:#1f5f6b}
.badge.manual{background:#fbf3e2;color:#8a6212}
.badge.warn{background:#fbeceb;color:#9e2f24}
.err{color:#a33228}
</style>
